import {
  type ClientRect,
  type Collision,
  type CollisionDescriptor,
  type CollisionDetection,
  type DragOverEvent,
  type Modifier,
} from '@dnd-kit/core';
import type {
  ColumnPinningState,
  Column,
  RowData,
  TableFeatures,
  CellData,
} from '@tanstack/react-table';

import Animations from '@dynatrace/strato-design-tokens/animations';

import * as columnSettingsDndStyles from './ColumnOrderSettingsDragAndDrop.sty.js';
import { getIsColumnPinned } from '../../../../features/ColumnPinning/column-pinning-utils.js';
import type { DragAndDropTransform } from '../../../../features/types/dnd.types.js';
import * as columnListItemStyles from '../DataTableColumnsListItem.sty.js';

export const COLUMNS_LIST_ITEM_BORDER_HEIGHT = 1;
export const DATA_TABLE_COLUMNS_LIST_ITEM_MIN_HEIGHT =
  32 + COLUMNS_LIST_ITEM_BORDER_HEIGHT;

// all columns in the settings modal that were transformed in the last onDragOver call
let lastAffectedItems = new Set<string>();

// child columns of a parent column that should be hidden during drag
// when items are virtualized and scroll back into view, they need to be hidden again
let hiddenChildColumns = new Set<string>();

// #region Transforms
/**
 * Transforms columns in the settings modal upwards or downwards to make space for the item which is currently being dragged.
 *
 * @param columnsList - the columns list element holding the columns in the settings modal.
 * @param event - contains "active" (item being dragged) and "over" ids (item where the active item is currently dragged over).
 * @param localColumnOrder - column order before dragging.
 * @param transformValue - the value by which the columns should be transformed (positive for down or negative for up).
 * @param activeColumn - the column which is currently being dragged.
 * @internal
 */
export function transformColumnsOnDragOver<
  TFeatures extends TableFeatures,
  TData extends RowData,
>(
  columnsList: HTMLElement | null,
  event: DragOverEvent,
  localColumnOrder: string[],
  transformValue: number,
  activeColumn: Column<TFeatures, TData> | undefined,
): void {
  if (!columnsList || !activeColumn) {
    return;
  }

  const { over, collisions } = event;
  if (!over || !collisions?.length) {
    return;
  }

  // keep track of all items that are transformed during the current onDragOver call
  const newAffectedColumns = new Set<string>();
  const { fromIndex, toIndex } = getDragIndexes(
    localColumnOrder,
    activeColumn.id,
    over.data.current?.columnId as string,
  );
  const adjustedTransformValue =
    transformValue + COLUMNS_LIST_ITEM_BORDER_HEIGHT;

  if (fromIndex < toIndex) {
    // only when dragging downwards:
    // when dragging a parent column, the drag overlay is moved above child columns as well,
    // so we need to ensure that the actual parent column, including its child columns, in the columns list are not transformed
    const adaptedFromIndex = fromIndex + activeColumn.columns.length;

    transformColumns(
      columnsList,
      localColumnOrder.slice(adaptedFromIndex + 1, toIndex + 1),
      -adjustedTransformValue,
      newAffectedColumns,
    );
  } else if (fromIndex > toIndex) {
    transformColumns(
      columnsList,
      localColumnOrder.slice(toIndex, fromIndex),
      adjustedTransformValue,
      newAffectedColumns,
    );
  }

  // reset items that were transformed in the last drag operation but are not in the current one
  transformColumns(columnsList, lastAffectedItems, 0);
  lastAffectedItems = newAffectedColumns;
}

/**
 * Gets the rendered element of an unpinned column in the settings modal with a given column id.
 *
 * @param columnsList - the columns list element holding the columns in the settings modal.
 * @param columnId - id of the column.
 * @returns the column in the settings modal with the given id, or null if not found.
 * @internal
 */
export function getColumnById(
  columnsList: HTMLElement,
  columnId: string,
): HTMLElement | null {
  return columnsList.querySelector<HTMLElement>(
    `[data-columnid="${CSS.escape(columnId)}"]:not([data-pinned-position])`,
  );
}

/**
 * Gets all column elements displayed in the column settings modal.
 *
 * @param columnsList - the columns list element holding the columns shown in the settings modal.
 * @returns all columns in the settings modal.
 * @internal
 */
export function getAllColumns(columnsList: HTMLElement): HTMLElement[] {
  const allLocalColumns =
    columnsList.querySelectorAll<HTMLElement>('[data-columnid]');
  return Array.from(allLocalColumns);
}

/**
 * Gets all column elements in the settings modal that are pinned or don't have the same parent id.
 *
 * @param columnsList - the columns list element holding the columns in the settings modal.
 * @param activeParentId - the parent id of the currently actively dragged column.
 * @returns all columns in the settings modal that are not valid drop targets.
 */
function getAllNonTargetColumns(
  columnsList: HTMLElement,
  activeParentId?: string,
): HTMLElement[] {
  const selector = activeParentId
    ? `[data-pinned-position], [data-columnid]:not([data-parentcolumnid="${CSS.escape(
        String(activeParentId),
      )}"])`
    : '[data-pinned-position]';

  const nonTargetColumns = columnsList.querySelectorAll<HTMLElement>(selector);

  return Array.from(nonTargetColumns);
}

/**
 * Moves the column in the settings modal along the y-axis by the given number of pixels.
 *
 * @param localColumn - the column in the settings modal to translate.
 * @param value - number of pixels (positive for down or negative for up).
 * @internal
 */
function translateColumnY(localColumn: HTMLElement, value: number): void {
  const transform = `translateY(${value}px)`;
  const transition = `transform ${Animations.SlideIn.Duration.Base} ${Animations.Move.Easing}`;
  localColumn.style.transform = transform;
  localColumn.style.transition = transition;
}

/**
 * Resets the transform style on all items in the columns list.
 *
 * @param localColumns - the columns in the settings modal to reset.
 */
export function resetAllColumnsStyles(localColumns: HTMLElement[]): void {
  for (const column of localColumns) {
    column.style.removeProperty('transform');
    column.style.removeProperty('transition');
    column.classList.remove(
      columnSettingsDndStyles.disabledNonTargetLocalColumn,
      columnListItemStyles.dragging,
    );
  }
  lastAffectedItems.clear();
  hiddenChildColumns.clear();
}

/**
 * Applies styles to visually indicate droppable targets for the currently active columns in the settings modal.
 * Pinned columns or columns that do not share the same parent as the dragged column will appear visually disabled.
 *
 * @param columnsList - the columns list element containing the columns to style.
 * @param activeParentId - the parent id of the currently actively dragged column.
 * @internal
 */
export function styleNonTargetColumns(
  columnsList: HTMLElement,
  activeParentId?: string,
): void {
  const nonTargetLocalColumns = getAllNonTargetColumns(
    columnsList,
    activeParentId,
  );

  for (const column of nonTargetLocalColumns) {
    column.classList.add(columnSettingsDndStyles.disabledNonTargetLocalColumn);
  }
}

/**
 * Applies the dragging class to the specified child columns.
 *
 * @param columnsList - the columns list element containing the columns.
 * @param childColumnIds - the ids of child columns to apply styles to.
 */
function styleDraggedChildColumns(
  columnsList: HTMLElement,
  childColumnIds: Iterable<string>,
): void {
  for (const columnId of childColumnIds) {
    const childColumn = getColumnById(columnsList, columnId);
    if (childColumn) {
      childColumn.classList.add(columnListItemStyles.dragging);
    }
  }
}

/**
 * Hides child columns during parent column drag and
 * tracks which columns should remain hidden during scrolling.
 * This is necessary to handle virtualization - when child columns scroll back into view,
 * they need to be hidden again.
 *
 * @param columnsList - the columns list element containing the columns.
 * @param childColumnIds - the ids of child columns to hide.
 * @internal
 */
export function hideChildColumns(
  columnsList: HTMLElement,
  childColumnIds: string[],
): void {
  // Clear previous tracking and set new ones
  hiddenChildColumns = new Set(childColumnIds);
  styleDraggedChildColumns(columnsList, childColumnIds);
}

/**
 * Reapplies the isDragging class to child columns that should be hidden.
 * Called when scrolling during drag to handle newly rendered virtualized items.
 *
 * @param columnsList - the columns list element containing the columns.
 * @internal
 */
export function hideChildColumnsOnScroll(columnsList: HTMLElement): void {
  if (hiddenChildColumns.size === 0) {
    return;
  }
  styleDraggedChildColumns(columnsList, hiddenChildColumns);
}

/**
 * Gets the source and destination indexes for a dragged column.
 *
 * @param localColumnOrder - contains the ids of the columns in order.
 * @param activeId - the id of the dragged column in the settings modal.
 * @param overId - the id of the column where the active column is currently dragged over.
 * @returns indexes from where a column is dragged and where it is dragged to.
 */
export function getDragIndexes(
  localColumnOrder: string[],
  activeId: string,
  overId: string,
): { fromIndex: number; toIndex: number } {
  const fromIndex = localColumnOrder.indexOf(activeId);
  const toIndex = localColumnOrder.indexOf(overId);
  return { fromIndex, toIndex };
}

/**
 * Transforms columns in the settings modal upwards or downwards to make space for the item being dragged.
 *
 * @param container - the container element holding the columns in the settings modal.
 * @param columnIds - the ids of columns to transform.
 * @param amountToTransform - number of pixels to translate (positive for down or negative for up).
 * @param newAffectedItems - set to save all items that are transformed during the current onDragOver call.
 */
function transformColumns(
  container: HTMLElement,
  columnIds: string[] | Set<string>,
  amountToTransform: number,
  newAffectedItems?: Set<string>,
): void {
  for (const columnId of columnIds) {
    const column = getColumnById(container, columnId);
    if (!column) {
      continue;
    }
    translateColumnY(column, amountToTransform);
    if (newAffectedItems) {
      newAffectedItems.add(columnId);
      lastAffectedItems.delete(columnId);
    }
  }
}
// #endregion Transforms

// #region CollisionDetection
// min local column height is 33px, to ensure the offset is in the top/bottom half: 33 / 2
const DATA_TABLE_COLUMNS_LIST_DND_COLLISION_OFFSET =
  DATA_TABLE_COLUMNS_LIST_ITEM_MIN_HEIGHT / 2;

/**
 * Gets a list of ordered leaf column ids for the given column based on the column order.
 *
 * @param column - the column to get the leaf columns for.
 * @param localColumnOrder - the local column order to use for filtering.
 * @returns an array of ordered leaf column ids for the given column, or an empty array if the column is already a leaf column.
 * @internal
 */
export function getLeafColumnsForColumn<
  TFeatures extends TableFeatures,
  TData extends RowData,
  TValue extends CellData = CellData,
>(
  column: Column<TFeatures, TData, TValue>,
  localColumnOrder: string[],
): string[] {
  if (column.columns.length === 0) {
    return [];
  }
  return localColumnOrder.filter((columnId) =>
    column.columns.some((col) => col.id === columnId),
  );
}

/**
 * Custom collision detection algorithm for drag and drop in the column settings modal.
 * Determines the closest droppable target within the Y-axis based on the dragging direction.
 *
 * @param param0 - Object containing the droppable containers, the droppable rects, the collision rect and
 *                 the active element, which are needed for the collision detection.
 * @returns An array of collision descriptors, sorted by the distance to the dragged column, or an empty array if none is found.
 * @internal
 */
function closestTargetWithinY({
  droppableContainers,
  droppableRects,
  collisionRect,
  active,
}: Parameters<CollisionDetection>[0]): Collision[] {
  const initialRect = active.rect.current.initial;
  if (initialRect === null) {
    return [];
  }

  const collisions: CollisionDescriptor[] = [];

  // When dragging a parent column, we also need to consider the height of all its child columns,
  // as they are moved together with the parent column.
  // Depending on the dragging direction, we take either the top or the bottom of the drag overlay to check for collisions
  // (minus a small offset, so the overlay and underlying list item have to overlap a bit, before they are swapped).
  const isDraggingUp = collisionRect.top <= initialRect.top;
  const collisionRectY = isDraggingUp
    ? collisionRect.top + DATA_TABLE_COLUMNS_LIST_DND_COLLISION_OFFSET
    : collisionRect.bottom - DATA_TABLE_COLUMNS_LIST_DND_COLLISION_OFFSET;

  for (const droppableContainer of droppableContainers) {
    const { id } = droppableContainer;
    const rect = droppableRects.get(id);
    if (rect === undefined) {
      continue;
    }

    if (rect.top <= collisionRectY && collisionRectY <= rect.bottom) {
      return [
        {
          id,
          data: { droppableContainer },
        },
      ];
    }

    // in case the dragged column's collisionRectY is not directly within any of the droppable containers,
    // we need to find the closest one. Therefore, we calculate the distance between
    // the droppable container's top/bottom and the collisionRectY coordinate.
    const droppableContainerY = isDraggingUp ? rect.top : rect.bottom;
    const distance = Math.abs(collisionRectY - droppableContainerY);
    collisions.push({ id, data: { droppableContainer, value: distance } });
  }

  return collisions.toSorted((a, b) => a.data.value - b.data.value);
}

/**
 * Information about the currently dragged column.
 * @internal
 */
export type DraggingColumnInfo<
  TFeatures extends TableFeatures,
  TData extends RowData,
> = {
  id: string;
  index: number;
  column: Column<TFeatures, TData>;
};

/**
 * Custom collision detection for column ordering in the column settings modal.
 * Filters out invalid drop targets based on the nested structure and column pinning state, and applies
 * the closestCenter collision detection on the remaining valid targets.
 *
 * @param localColumnOrder - the column order before dragging.
 * @param localColumnPinning - the column pinning state in the column settings modal before dragging.
 * @param draggingColumnInfo - information about the currently dragged column.
 * @returns dnd-kit CollisionDetection
 * @internal
 */
export function createColumnOrderSettingsCollisionDetection<
  TFeatures extends TableFeatures,
  TData extends RowData,
>(
  localColumnOrder: string[],
  localColumnPinning: ColumnPinningState,
  draggingColumnInfo: DraggingColumnInfo<TFeatures, TData> | null,
): CollisionDetection {
  return ({ active, droppableContainers, ...rest }) => {
    if (!draggingColumnInfo) {
      return [];
    }

    const { column: activeColumn, index: activeIndex } = draggingColumnInfo;
    if (activeColumn === undefined) {
      return [];
    }
    const activeParentId = activeColumn.parent?.id;

    const validTargets = droppableContainers.filter((target) => {
      const currentTarget = target.data.current;
      const targetColumn = currentTarget?.column as Column<TFeatures, TData>;
      const targetParentColumn = targetColumn.parent;
      const targetIndex = currentTarget?.columnIndex as number;

      // skip the active column itself
      if (activeIndex === targetIndex) {
        return true;
      }

      const hasSameParent = targetParentColumn?.id === activeParentId;

      // the active column is a child column, so only allow dropping on columns with the same parent
      if (activeParentId !== undefined) {
        return hasSameParent;
      }

      // the active column is either a standalone column or a parent column
      // therefore, only allow dropping before or after the entire group of child columns
      if (targetColumn.columns.length > 0) {
        // the currently active column is dragged upwards, only allow dropping before the first child column
        return activeIndex > targetIndex;
      }
      if (targetParentColumn?.id !== undefined) {
        const orderedChildIds = getLeafColumnsForColumn(
          targetParentColumn,
          localColumnOrder,
        );
        const lastUnpinnedChildId = orderedChildIds.findLast(
          (childId) => !getIsColumnPinned(childId, localColumnPinning),
        );

        if (targetColumn.id === lastUnpinnedChildId) {
          // the currently active column is dragged downwards, only allow dropping after the last child column
          return activeIndex < targetIndex;
        }
      }
      // for all remaining columns, only allow dropping on columns with the same parent
      return hasSameParent;
    });

    return closestTargetWithinY({
      active,
      droppableContainers: validTargets,
      ...rest,
    });
  };
}
// #endregion CollisionDetection

// #region Modifiers
/**
 * Restricts the vertical movement of an element within the specified bounds.
 *
 * @param transform - Transform values of the currently dragged element
 * @param rect - The bounding rectangle of the element being transformed.
 * @param verticalBounds - The bounds within which the element should be restricted.
 * @returns The updated transform object with restricted y value.
 */
function restrictToVerticalBounds(
  transform: DragAndDropTransform,
  rect: ClientRect,
  verticalBounds: {
    top: number;
    bottom: number;
  },
): DragAndDropTransform {
  const value = {
    ...transform,
  };

  if (rect.top + transform.y <= verticalBounds.top) {
    value.y = verticalBounds.top - rect.top;
  } else if (rect.bottom + transform.y >= verticalBounds.bottom) {
    value.y = verticalBounds.bottom - rect.bottom;
  }

  return value;
}

/**
 * Creates a modifier that restricts the drag overlay to the columns list area within the settings modal.
 *
 * @param modalContent - The columns list element to restrict dragging within.
 * @returns dnd-kit modifier
 * @internal
 */
export function createRestrictToColumnsListAreaModifier(
  modalContent: HTMLDivElement | null,
): Modifier {
  return ({ transform, draggingNodeRect }) => {
    if (!draggingNodeRect || modalContent === null) {
      return transform;
    }

    // Restrict dragging to the vertical axis
    transform.x = 0;

    const columnsListRect = modalContent.getBoundingClientRect();
    const verticalBounds = {
      top: columnsListRect.top,
      bottom: columnsListRect.bottom,
    };

    // Only unpinned columns are reorderable; clamp to section headers rather than
    // column elements — headers don't transform during drag, preventing jitter.
    const firstUnpinned = modalContent.querySelector(
      '[data-columnid]:not([data-pinned-position])',
    );
    const unpinnedHeader = firstUnpinned?.previousElementSibling;
    if (unpinnedHeader && !unpinnedHeader.hasAttribute('data-columnid')) {
      verticalBounds.top = Math.max(
        verticalBounds.top,
        unpinnedHeader.getBoundingClientRect().bottom,
      );
    }

    return restrictToVerticalBounds(
      transform,
      draggingNodeRect,
      verticalBounds,
    );
  };
}
// #endregion Modifiers

/**
 * Gets the drag handle button for a specific column in the column settings modal.
 * @param columnId - the id of the column to get the drag handle for.
 * @param columnsList - the columns list element holding the columns in the settings modal.
 * @returns the drag handle button for the specified column, or null if not found.
 */
export function getDragHandleForColumn(
  columnId: string,
  columnsList: HTMLElement | null,
): HTMLButtonElement | null {
  if (columnsList === null) {
    return null;
  }

  return (
    columnsList?.querySelector<HTMLButtonElement>(
      `[data-columnid="${CSS.escape(
        columnId,
      )}"]:not([data-pinned-position]) button`,
    ) ?? null
  );
}
